import { Alert, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { FormInstance } from 'antd/lib'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import { BUYBACK_FIELD, BuybackFormValues } from '../../../common/buybackTypes'
import {
  Maybe,
  usePropertyBuybackCalculationsQuery,
} from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import { dateSorter } from '../../utils/propertyMonitoringHelpers'
import { ImgSrc } from '../../utils/useMapImage'
import { CustomDate, Min } from '../DateFormat/DateFormat'
import { PropertyDetailBuybackHistoryTableAction } from '../PropertyDetailBuybackHistoryTableAction/PropertyDetailBuybackHistoryTableAction'

export interface BuybackTableItem {
  id: string | undefined
  accountId?: string
  createdAt?: string | Date
  title?: Maybe<string>
  formData?: Maybe<string>
  calculatorVersion?: Maybe<string>
}

export interface PropertyDetailBuybackHistoryTableProps {
  imgSrc?: ImgSrc
  propertyId: string
  form?: FormInstance<BuybackFormValues>
}

export const PropertyDetailBuybackHistoryTable: FunctionComponent<PropertyDetailBuybackHistoryTableProps> =
  props => {
    const tenantId = useAppSelector(state => state.myProfile.tenantId)

    const [historyBuyback] = usePropertyBuybackCalculationsQuery({
      variables: { accountId: tenantId, propertyId: props.propertyId },
    })

    const historyData =
      (historyBuyback.data?.allBuybackCalculations
        ?.nodes as BuybackTableItem[]) || []

    const selectBuyback = useCallback(
      (item: BuybackTableItem) => {
        const formData = item.formData ? JSON.parse(item.formData) : {}
        const value = { ...formData, [BUYBACK_FIELD.CalculationId]: item.id }

        props.form?.resetFields()
        props.form?.setFieldsValue(value)
      },
      [props.form]
    )

    const columns: ColumnsType<BuybackTableItem> = useMemo(
      () => [
        {
          title: 'Datum',
          dataIndex: 'createdAt',
          className: 'w-12',
          sorter: dateSorter('createdAt'),
          render: (date: string) => (
            <Tooltip
              title={
                <div>
                  <Min>{date}</Min>
                </div>
              }
            >
              <div>
                <CustomDate format="dd.M.yy">{date}</CustomDate>
              </div>
            </Tooltip>
          ),
        },
        {
          title: 'Označení',
          dataIndex: 'title',
        },
        {
          title: 'Akce',
          dataIndex: 'id',
          className: 'text-right w-28',
          render: (_id, record) => (
            <PropertyDetailBuybackHistoryTableAction
              record={record}
              onSelect={selectBuyback}
            />
          ),
        },
      ],
      [selectBuyback]
    )

    if (!historyData?.length) return null
    if (historyBuyback.error)
      return (
        <Alert type="error" message="Chyba při načítání historie kalkulací." />
      )

    return (
      <div className="print:!hidden">
        <div className="text-lg mb-4">Historie kalkulací</div>
        <Table
          dataSource={historyData}
          columns={columns}
          size="small"
          pagination={false}
          rowKey="id"
        />
      </div>
    )
  }
